/**
 * Image compression utility to ensure images stay strictly under maxSizeBytes (default 300KB = 300 * 1024 bytes).
 * Uses HTML5 Canvas for fast, zero-dependency client-side image resizing and compression.
 */
export const compressImage = (file, maxSizeBytes = 300 * 1024) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      return resolve(null);
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Max dimension scaling to maintain aspect ratio while reducing memory
        const maxDimension = 1200;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Iteratively lower quality until image size is strictly under maxSizeBytes (300KB)
        let quality = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);

        // Standard base64 overhead calculation: size in bytes approx dataUrl.length * 0.75
        while (dataUrl.length * 0.75 > maxSizeBytes && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }

        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

/**
 * Converts a base64 Data URL string to a binary File object for FormData uploads.
 */
export const dataURLtoFile = (dataurl, filename = "image.jpg") => {
  if (!dataurl || typeof dataurl !== "string") return null;
  const arr = dataurl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bstr = atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};
